import { useCallback, useEffect, useState } from 'react';
import { leave } from '../api/client';

function lastMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const signed = (v) => (v > 0 ? `+${v}` : `${v}`);

/**
 * HR view of everyone's leave. A balance is changed by entering the new
 * available figure; the server stores the difference as an adjustment with the
 * reason, so accrual history is never overwritten.
 */
export default function LeaveAdmin({ me, onError, onNotice }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [grid, setGrid] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);   // { row, type, cell }
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(lastMonth());
  const [accruing, setAccruing] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const [g, h] = await Promise.all([leave.adminBalances(year), leave.adjustments()]);
      setGrid(g);
      setHistory(h);
    } catch (e) {
      onError(e.message);
    }
  }, [year, onError]);

  useEffect(() => { load(); }, [load]);

  const open = (row, type) => {
    const cell = row.balances[String(type.id)];
    setEditing({ row, type, cell });
    setValue(String(cell.available));
    setReason('');
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await leave.setBalance({
        employeeId: editing.row.employeeId,
        leaveTypeId: editing.type.id,
        year,
        available: Number(value),
        reason,
      });
      onNotice(`${r.employeeCode} ${r.leaveCode}: ${r.previousAvailable} → ${r.newAvailable} (${signed(r.delta)})`);
      setEditing(null);
      await load();
    } catch (err) {
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const accrue = async () => {
    if (!window.confirm(`Credit ${month} accrual to all on-roll employees? A month already credited is skipped.`)) return;
    setAccruing(true);
    try {
      const r = await leave.runAccrual(month);
      onNotice(`Accrual ${r.month}: ${r.balancesCredited} credited, ${r.alreadyAccrued} already done, ${r.employeesNotEligible} not eligible.`);
      await load();
    } catch (err) {
      onError(err.message);
    } finally {
      setAccruing(false);
    }
  };

  if (!grid) return <p className="muted">Loading…</p>;

  const q = filter.trim().toLowerCase();
  const rows = grid.employees.filter((r) =>
    !q || `${r.employeeCode} ${r.name}`.toLowerCase().includes(q));
  const newNum = Number(value);
  const delta = editing && value !== '' && !Number.isNaN(newNum)
    ? Math.round((newNum - editing.cell.available) * 100) / 100 : null;

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>Leave balances</h2>
          <p className="muted">
            Click a balance to set it. The change is recorded with your reason; accrued and used days are kept.
          </p>
        </div>
        <div className="toolbar">
          <select value={year} onChange={(e) => { setYear(Number(e.target.value)); setEditing(null); }}>
            {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          <button className="btn" onClick={accrue} disabled={accruing || !month}>
            {accruing ? 'Running…' : 'Run accrual'}
          </button>
        </div>
      </div>

      {editing && (
        <form className="card form-grid" onSubmit={save} style={{ marginTop: 14 }}>
          <h3 className="span-all">
            {editing.row.employeeCode} · {editing.row.name} — {editing.type.name} ({year})
          </h3>
          <p className="span-all small muted">
            Now {editing.cell.available} available
            {' = '}{editing.cell.carriedForward} c/f + {editing.cell.accrued} accrued
            {' '}{signed(editing.cell.adjustment)} adjusted − {editing.cell.used} used − {editing.cell.pending} pending
          </p>
          <label>
            New available balance
            <input type="number" step="0.5" required autoFocus value={value}
                   min={editing.type.allowNegative ? undefined : 0}
                   onChange={(e) => setValue(e.target.value)} />
            {delta !== null && delta !== 0 && (
              <span className="small muted">{signed(delta)} day(s)</span>
            )}
          </label>
          <label>
            Reason (kept in history)
            <input required minLength={3} value={reason} placeholder="e.g. Opening balance from previous HR system"
                   onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="span-all">
            <button className="btn btn-primary" disabled={saving || delta === 0 || delta === null}>
              {saving ? 'Saving…' : 'Save balance'}
            </button>{' '}
            <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="card" style={{ marginTop: 14 }}>
        <input placeholder="Filter by name or code" value={filter}
               onChange={(e) => setFilter(e.target.value)} style={{ marginBottom: 10, maxWidth: 260 }} />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                {grid.types.map((t) => <th key={t.id} className="num" title={t.name}>{t.code}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const self = r.employeeId === me.employeeId;
                return (
                  <tr key={r.employeeId}>
                    <td>
                      <strong>{r.employeeCode}</strong> {r.name}
                      {!r.dateOfJoining && <span className="small muted"> · no joining date</span>}
                    </td>
                    {grid.types.map((t) => {
                      const c = r.balances[String(t.id)];
                      const on = editing && editing.row.employeeId === r.employeeId && editing.type.id === t.id;
                      return (
                        <td key={t.id} className="num">
                          {self ? (
                            <span title="Another HR/admin has to change your own balance">{c.available}</span>
                          ) : (
                            <button type="button" className={`btn btn-sm ${on ? 'btn-primary' : ''}`}
                                    onClick={() => open(r, t)}
                                    title={`${c.accrued} accrued · ${c.carriedForward} c/f · ${signed(c.adjustment)} adj · ${c.used} used · ${c.pending} pending`}>
                              {c.available}
                            </button>
                          )}
                          {c.adjustment !== 0 && <div className="small muted">{signed(c.adjustment)} adj</div>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3>Recent adjustments</h3>
        {history.length === 0 ? (
          <p className="muted">No manual changes yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>When</th><th>Employee</th><th>Type</th><th className="num">From → To</th><th>Reason</th><th>By</th></tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="small">{String(h.at).replace('T', ' ').slice(0, 16)}</td>
                    <td>{h.employee}</td>
                    <td>{h.leaveCode} {h.year}</td>
                    <td className="num">{h.from} → {h.to} <span className="small muted">({signed(h.delta)})</span></td>
                    <td>{h.reason}</td>
                    <td>{h.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
